import React, { Component } from 'react'
import PropTypes from 'prop-types'
import uuid from 'uuid'
import { withRouter } from 'react-router'
import { goBack, push } from 'connected-react-router'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { notification, setPage, modal, logError } from 'redux/modules/app'
import {
  contacts as contactsApi,
  persons as personsApi
} from 'redux/modules/api'
import { createSchema, updateSchema } from 'models/contacts'
import appConfig from 'config'

function decorator(config = {}) {
  return ComposedComponent => {
    const enhance = compose(
      withRouter,
      connect(
        ({ api, auth }) => ({
          contacts: api.contacts,
          activeOrganization: auth.organization,
          user: auth.user
        }),
        {
          ...contactsApi,
          notification,
          goBack,
          push,
          setPage,
          logError,
          modal,
          uploadPersonImage: personsApi.uploadPersonImage,
          deletePersonImage: personsApi.deletePersonImage
        }
      )
    )

    class Contacts extends Component {
      static propTypes = {
        activeOrganization: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
        params: PropTypes.object.isRequired,
        decoFilter: PropTypes.string,
        contacts: PropTypes.object.isRequired,
        getOrganizationContacts: PropTypes.func.isRequired,
        getOrganizationContact: PropTypes.func.isRequired,
        getOrganizationContactForActiveUser: PropTypes.func.isRequired,
        clearOrganizationContactDetail: PropTypes.func.isRequired,
        updateOrganizationContact: PropTypes.func.isRequired,
        deleteOrganizationContact: PropTypes.func.isRequired,
        createOrganizationContact: PropTypes.func.isRequired,
        getOrganizationContactAllowedRoles: PropTypes.func.isRequired,
        uploadPersonImage: PropTypes.func.isRequired,
        deletePersonImage: PropTypes.func.isRequired,
        notification: PropTypes.func.isRequired,
        goBack: PropTypes.func.isRequired,
        logError: PropTypes.func.isRequired,
        push: PropTypes.func.isRequired,
        setPage: PropTypes.func.isRequired,
        active: PropTypes.object.isRequired
      }

      static defaultProps = {
        params: {}
      }

      constructor(props) {
        super(props)
        this.key = uuid.v1()
      }

      componentDidMount() {
        config.list && this.handleFetchOrganizationContacts()
        config.detail && this.handleGetOrganizationContact()
        config.clearDetail && this.handleClearOrganizationContactDetail()
        config.active && this.handleGetOrganizationContactForActiveUser()
      }

      componentWillReceiveProps(nextProps) {
        const { contacts } = this.props
        const { filter, limit, offset, sort, invalidate } = nextProps.contacts

        if (
          contacts.filter !== filter ||
          contacts.limit !== limit ||
          contacts.offset !== offset ||
          contacts.sort !== sort ||
          contacts.invalidate !== invalidate
        ) {
          config.list &&
          this.handleGetOrganizationContacts(
            nextProps.contacts,
            this.handleGetBaseFilter(),
              config.onlyActive
          )
        }
      }

      handleFetchOrganizationContacts = () => {
        const { contacts } = this.props

        if (config.disableLimit) {
          contacts.limit = undefined
        }

        this.handleGetOrganizationContacts(contacts, this.handleGetBaseFilter(), config.onlyActive)
      }

      handleGetBaseFilter = () => {
        const { decoFilter } = this.props

        switch (decoFilter || config.filter) {
          case 'naoContacts':
            return `organization.id=${this.handleGetOrganizationId()}`
          case 'isAwardLeader':
            return 'isAwardLeader=1'
          default:
            return ''
        }
      }

      handleGetOrganizationContacts = (contacts, baseFilter, onlyActive) =>
        this.props.getOrganizationContacts(
          this.handleGetOrganizationId(),
          contacts,
          baseFilter,
          { onlyActive }
        )

      handleGetOrganizationContactForActiveUser = () => {
        const activeOrganization = this.props.activeOrganization
        const user = this.props.user

        this.props.getOrganizationContactForActiveUser(activeOrganization.id, user.personId)
            .then(({ body = [] }) => {
              const contactId = get(body[0], 'id')
                  ? `(${body[0].globalId || `ID: ${body[0].id}`})`
                  : ''

              this.props.setPage(
                  `${get(body[0], 'firstName') || get(body[0], 'person.firstName')} ${get(body[0], 'lastName') || get(body[0], 'person.lastName')} ${contactId}`,
                  false
              )
            })
      }

      handleGetOrganizationContact = () => {
        const { params } = this.props

        this.getOrganizationContact(get(params, 'organizationId'), get(params, 'contactId'))
      }

      handleGetCustomOrganizationContact = (orgId, contactId) => {
        this.getOrganizationContact(orgId, contactId)
      }

      getOrganizationContact = (orgId, contactId) => {
        this.props.getOrganizationContact(orgId, contactId)
          .then(({ body = [] }) => {
            const contactId = get(body[0], 'id')
              ? `(${body[0].globalId || `ID: ${body[0].id}`})`
              : ''

            this.props.setPage(
              `${get(body[0], 'firstName') || get(body[0], 'person.firstName')} ${get(body[0], 'lastName') || get(body[0], 'person.lastName')} ${contactId}`,
              false
            )
          })
      }

      handleGetOrganizationContactAllowedRoles = rolesForOrganizationId => {
        const { params } = this.props

        return this.props.getOrganizationContactAllowedRoles(
          params.contactId,
          rolesForOrganizationId
        )
      }

      handleClearOrganizationContactDetail = () => {
        this.props.clearOrganizationContactDetail()
      }

      handleGetOrganizationId = () =>{
        return get(this.props, 'match.params.id') || this.props.activeOrganization.id
      }

      handleCreateContact = fields =>
        this.props
          .createOrganizationContact(
            this.handleGetOrganizationId(),
            createSchema(fields)
          )
          .then(this.handleCreateSuccess)
          .catch(this.handleError)

      handleDeleteContact = (id, fields = {}) =>
        this.props
          .deleteOrganizationContact(id, { reason: fields.reason })
          .then(this.handleDeleteSuccess)
          .catch(this.handleError)

      handlePrepareUpdateContact = (personId, fields, opts = {}) => {
        const { contacts } = this.props

        if (fields.file) {
          return this.props
            .uploadPersonImage(personId, fields.file)
            .then(({ body = {} }) => {
              this.handleUpdateContact({fields, photoUrl: body.photoUrl}, opts)
            })
            .catch(this.handleError)
        }

        if (get(contacts, 'detail.person.photoUrl') && !fields.photoUrl) {
          this.props.deletePersonImage(personId).then(() => {
            this.handleUpdateContact(fields, opts)
          })
          return
        }

        return this.handleUpdateContact(fields, opts)
      }

      handleUpdateContact = (fields, opts = {}) => {
        this.props
          .updateOrganizationContact(fields.id, updateSchema(fields))
          .then(() =>{
            this.handleUpdateSuccess()

            if(get(opts, 'isMfaChanged')){
                this.props.push(`${appConfig.publicPath}/logout`)
                return
              }
          })
          .catch(this.handleError)
      }

      handleCreateSuccess = ({ body }) => {
        this.props.notification('Contact has been created', {
          status: 'success',
          intlKey: 'notifications.contact_has_been_created'
        })
        this.props.push(
          `${appConfig.publicPath}/organizations/${this.handleGetOrganizationId()}/contacts/${get(body, 'id')}`
        )
      }

      handleUpdateSuccess = (opts = {}) => {
        this.props.notification('Contact has been updated', {
          status: 'success',
          intlKey: 'notifications.contact_has_been_updated'
        })
        this.handleGetOrganizationContact(opts)
      }

      handleDeleteSuccess = () => {
        this.props.notification('Contact has been removed', {
          status: 'success',
          intlKey: 'notifications.contact_has_been_removed'
        })
        this.props.goBack()
      }

      handleError = err => {
        console.debug('error', err.body && err.body.userMessage)
        this.props.logError(err)
      }

      render() {
        return (
          <ComposedComponent
            {...this.props}
            key={this.key}
            onCreateContact={this.handleCreateContact}
            onUpdateContact={this.handlePrepareUpdateContact}
            onDeleteContact={this.handleDeleteContact}
            fetchCustomOrganizationContact={this.handleGetCustomOrganizationContact}
            fetchOrganizationContacts={this.handleFetchOrganizationContacts}
            fetchOrganizationContactAllowedRoles={
              this.handleGetOrganizationContactAllowedRoles
            }
            getActiveOrganizationContact={this.handleGetOrganizationContactForActiveUser}
          />
        )
      }
    }

    return enhance(Contacts)
  }
}

export default decorator
